<template>
  <portlet
      :icon="'fad fa-file-contract'"
      :title="'Contract Toevoegen'"
      :actionsBlock="true"
      :footer="true"
  >
    <template slot="actionsBlock">
      <router-link tag="button" :to="{name:'contracts'}" class="btn btn-secondary">
        <i class="fas fa-long-arrow-left mr-2"></i>
        Terug
      </router-link>
    </template>

    <template slot="portletBody">
      <form @submit.prevent="saveContract">
        <div class="row">
          <div class="form-group col-lg-4">
            <label for="candidate">Kandidaat <span class="text-danger">*</span></label>
            <select v-validate="'required'" name="candidateId" id="candidate" class="form-control"
                    v-model="contract.candidateId" @change="onCandidateSelect(contract.candidateId)">
              <option :value="null" disabled>Selecteer een kandidaat</option>
              <option v-for="candidate in candidates" :key="candidate.id" :value="candidate.id">
                {{ candidate.firstName }} {{ candidate.lastName }}
              </option>
            </select>
            <span v-if="errors.first('candidateId')" class="small text-danger">Dit veld is verplicht</span>
          </div>
          <div class="form-group col-lg-4">
            <label for="firstName">Voornaam <span class="text-danger">*</span></label>
            <input v-validate="'required'" name="firstName" id="firstName" type="text" class="form-control"
                   v-model="contract.firstName" readonly/>
            <span v-if="errors.first('firstName')" class="small text-danger">Dit veld is verplicht</span>
          </div>
          <div class="form-group col-lg-4">
            <label for="lastName">Achternaam <span class="text-danger">*</span></label>
            <input v-validate="'required'" name="lastName" id="lastName" type="text" class="form-control"
                   v-model="contract.lastName" readonly/>
            <span v-if="errors.first('lastName')" class="small text-danger">Dit veld is verplicht</span>
          </div>
          <div class="form-group col-lg-4">
            <label for="bsn">BSN <span class="text-danger">*</span></label>
            <input v-validate="'required|numeric|digits:9|bsn'" name="bsn" id="bsn" type="text" maxlength="9" class="form-control" v-model="contract.bsn" data-private/>
            <span v-if="errors.firstByRule('bsn', 'bsn')" class="small text-danger">Dit BSN klopt niet (elfproef). Controleer het nummer.</span>
            <span v-else-if="errors.first('bsn')" class="small text-danger">Dit veld is verplicht en moet 9 cijfers bevatten.</span>
            <small v-else-if="bsnFromStamkaart" class="d-block text-muted">Overgenomen van de stamkaart</small>
            <div v-if="bsnDiffersFromStamkaart" class="mt-2" style="background-color: #fff8e6; border: 1px solid #f0ad4e; border-radius: 4px; padding: 8px 12px; color: #8a6d3b;">
              <i class="fas fa-exclamation-triangle mr-2"></i>
              Dit BSN wijkt af van het BSN op de stamkaart ({{ maskedStamkaartBsn }}). De stamkaart wordt niet aangepast. Laat een admin dit controleren.
            </div>
            <div class="form-group inline-checkboxes">
              <div class="custom-control custom-checkbox">
                <input
                    v-model="contract.isDutch"
                    type="checkbox"
                    id="isDutch"
                    class="custom-control-input"/>
                <label class="custom-control-label" for="isDutch">Heeft de medewerker een Nederlandse nationaliteit?</label>
              </div>
            </div>
          </div>
          <div class="form-group col-lg-4">
            <label for="customer">Bedrijf <span class="text-danger">*</span></label>
            <select v-validate="'required'" name="customerId" id="customer" class="form-control"
                    v-model="contract.customerId" @change="onCustomerSelect(contract.customerId)">
              <option :value="null" disabled>Selecteer een bedrijf</option>
              <option v-for="customer in activeCustomers" :key="customer.id" :value="customer.id">
                {{ customer.name }}
              </option>
            </select>
            <span v-if="errors.first('customerId')" class="small text-danger">Dit veld is verplicht</span>
            <div v-if="selectedCustomer" class="mt-2">
              <p class="mb-0">{{ selectedCustomer.street }} {{ selectedCustomer.nr }}</p>
              <p class="mb-0">{{ selectedCustomer.zipcode }} {{ selectedCustomer.location }}</p>
            </div>
          </div>
          <div class="form-group col-lg-4">
            <label for="shifts">Ploegen <span class="text-danger">*</span></label>
            <select v-validate="'required'" name="shifts" id="shifts" class="form-control"
                    v-model="contract.shifts">
              <option v-for="shift in shiftTypes" :key="shift" :value="shift">{{ shift }}</option>
            </select>
            <span v-if="errors.first('shifts')" class="small text-danger">Dit veld is verplicht</span>
          </div>
          <div class="form-group col-lg-4">
            <label for="shiftAddition">Ploegentoeslag <span class="text-danger">*</span></label>
            <div class="input-group">
              <div class="input-group-prepend">
                <button class="btn btn-outline-secondary" type="button" @click="decrementShiftAddition">-</button>
              </div>
              <input v-validate="'required'" name="shiftAddition" id="shiftAddition" type="number" step="0.01" min="0" class="form-control"
                     v-model.number="contract.shiftAddition"/>
              <div class="input-group-append">
                <button class="btn btn-outline-secondary" type="button" @click="incrementShiftAddition">+</button>
              </div>
            </div>
            <span v-if="errors.first('shiftAddition')" class="small text-danger">Dit veld is verplicht</span>
          </div>
          <div class="form-group col-lg-4">
            <label for="function">Functie <span class="text-danger">*</span></label>
            <input v-validate="'required'" name="function" id="function" type="text" class="form-control"
                   v-model="contract.function"/>
            <span v-if="errors.first('function')" class="small text-danger">Dit veld is verplicht</span>
          </div>
          <div class="form-group col-lg-4">
            <label for="startDate">Startdatum <span class="text-danger">*</span></label>
            <datepicker
                v-validate="'required'"
                id="startDate"
                name="startDate"
                v-model="contract.startDate"
                :language="nl"
                :bootstrap-styling="true"
            ></datepicker>
            <span v-if="errors.first('startDate')" class="small text-danger">Dit veld is verplicht</span>
          </div>
          <div class="form-group col-lg-4">
            <label for="scale">Schaal <span class="text-danger">*</span></label>
            <input v-validate="'required'" name="scale" id="scale" type="text" class="form-control"
                   v-model="contract.scale"/>
            <span v-if="errors.first('scale')" class="small text-danger">Dit veld is verplicht</span>
          </div>
          <div class="form-group col-lg-4">
            <label for="step">Trede <span class="text-danger">*</span></label>
            <input v-validate="'required'" name="step" id="step" type="text" class="form-control"
                   v-model="contract.step"/>
            <span v-if="errors.first('step')" class="small text-danger">Dit veld is verplicht</span>
          </div>
          <div class="form-group col-lg-4">
            <label for="hourlyWage">Uurloon <span class="text-danger">*</span></label>
            <div class="input-group">
              <div class="input-group-prepend">
                <button class="btn btn-outline-secondary" type="button" @click="decrementHourlyWage">-</button>
              </div>
              <input v-validate="'required'" name="hourlyWage" id="hourlyWage" type="number" step="0.01" min="0" class="form-control"
                     v-model.number="contract.hourlyWage"/>
              <div class="input-group-append">
                <button class="btn btn-outline-secondary" type="button" @click="incrementHourlyWage">+</button>
              </div>
            </div>
            <span v-if="errors.first('hourlyWage')" class="small text-danger">Dit veld is verplicht</span>
          </div>
          <div class="form-group col-lg-4">
            <label for="factor">Factor <span class="text-danger">*</span></label>
            <div class="input-group">
              <div class="input-group-prepend">
                <button class="btn btn-outline-secondary" type="button" @click="decrementFactor">-</button>
              </div>
              <input v-validate="'required'" name="factor" id="factor" type="number" step="0.1" min="0" class="form-control"
                     v-model.number="contract.factor"/>
              <div class="input-group-append">
                <button class="btn btn-outline-secondary" type="button" @click="incrementFactor">+</button>
              </div>
            </div>
            <span v-if="errors.first('factor')" class="small text-danger">Dit veld is verplicht</span>
          </div>
          <div class="form-group col-lg-4">
            <label for="paymentFrequency">Betaalfrequentie <span class="text-danger">*</span></label>
            <select v-validate="'required'" name="paymentFrequency" id="paymentFrequency" class="form-control"
                    v-model="contract.paymentFrequency">
              <option v-for="frequency in paymentFrequencies" :key="frequency" :value="frequency">{{ frequency }}</option>
            </select>
            <span v-if="errors.first('paymentFrequency')" class="small text-danger">Dit veld is verplicht</span>
          </div>
          <div class="form-group col-lg-4">
            <label for="wkr">WKR per week</label>
            <input name="wkr" id="wkr" type="text" class="form-control" v-model="contract.wkr"/>
          </div>
          <div class="form-group col-lg-4">
            <label for="contractLanguage">In welke taal wil je het contract laten maken? <span class="text-danger">*</span></label>
            <select v-validate="'required'" name="contractLanguage" id="contractLanguage" class="form-control"
                    v-model="contract.contractLanguage">
              <option :value="null" disabled>Selecteer een taal</option>
              <option v-for="language in contractLanguages" :key="language.value" :value="language.value">{{ language.label }}</option>
            </select>
            <span v-if="errors.first('contractLanguage')" class="small text-danger">Dit veld is verplicht</span>
          </div>
          <div class="col-lg-6">
            <div class="form-group" v-for="day in daysOfWeek" :key="day">
              <div class="row">
                <div class="col-lg-4">
                  <label :for="day">{{ day }}</label>
                  <div class="input-group">
                    <input :name="day + '_start'" :id="day + '_start'" type="time" class="form-control" v-model="contract.workingHours[day].start" :disabled="contract.workingHours[day].notWorking"/>
                    <input :name="day + '_eind'" :id="day + '_eind'" type="time" class="form-control" v-model="contract.workingHours[day].end" :disabled="contract.workingHours[day].notWorking"/>
                  </div>
                </div>
                <div class="col-lg-4" style="padding-top: 35px;">
                  <div class="custom-control custom-checkbox">
                    <input
                        v-model="contract.workingHours[day].notWorking"
                        type="checkbox"
                        :id="day + '_notWorking'"
                        class="custom-control-input"/>
                    <label class="custom-control-label" :for="day + '_notWorking'">Niet werkzaam</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="row">
              <div v-if="hasRecentPlacement" class="col-lg-12 mb-2">
                <div style="background-color: #e6f7f7; border: 1px solid #25b7bc; border-radius: 4px; padding: 12px 16px; color: #1a8a8e;">
                  <i class="fas fa-info-circle mr-2"></i>
                  Deze medewerker heeft een plaatsing in de afgelopen 4 weken. Documenten uploaden is niet verplicht.
                </div>
              </div>
              <div class="form-group col-lg-6">
                <label>ID (voorkant) <span class="text-danger" v-if="!hasRecentPlacement">*</span></label>
                <file-select :disabled="hasRecentPlacement" v-validate="{ required: !hasRecentPlacement }" name="idFront" v-model="contract.idFront"></file-select>
                <span v-if="errors.first('idFront')" class="small text-danger">Dit veld is verplicht</span>
              </div>
              <div class="form-group col-lg-6">
                <label>ID (achterkant) <span class="text-danger" v-if="!hasRecentPlacement">*</span></label>
                <file-select :disabled="hasRecentPlacement" v-validate="{ required: !hasRecentPlacement }" name="idBack" v-model="contract.idBack"></file-select>
                <span v-if="errors.first('idBack')" class="small text-danger">Dit veld is verplicht</span>
              </div>
              <div class="form-group col-lg-6">
                <label>Bankpas <span class="text-danger" v-if="!hasRecentPlacement">*</span></label>
                <file-select :disabled="hasRecentPlacement" v-validate="{ required: !hasRecentPlacement }" name="bankCard" v-model="contract.bankCard"></file-select>
                <span v-if="errors.first('bankCard')" class="small text-danger">Dit veld is verplicht</span>
              </div>
              <div class="form-group col-lg-6">
                <label>Rijbewijs</label>
                <file-select :disabled="hasRecentPlacement" name="driversLicense" v-model="contract.driversLicense"></file-select>
              </div>
              <div class="form-group col-lg-6">
                <label>BSN Document <span class="text-danger" v-if="!contract.isDutch && !hasRecentPlacement">*</span></label>
                <file-select name="bsnDocument" v-model="contract.bsnDocument" :disabled="contract.isDutch || hasRecentPlacement" v-validate="{ required: !contract.isDutch && !hasRecentPlacement }"></file-select>
                <span v-if="errors.first('bsnDocument')" class="small text-danger">Dit veld is verplicht</span>
              </div>
              <div class="form-group col-lg-12">
                <label for="remark">Opmerking</label>
                <textarea id="remark" name="remark" class="form-control" v-model="contract.remark"></textarea>
              </div>
            </div>
          </div>
        </div>

      </form>
    </template>
    <div slot="portletFooter" class="col text-right">
      <button @click="saveContract" type="submit" class="btn btn-primary">
        <i class="fal fa-save mr-2"></i> Opslaan
      </button>
    </div>
  </portlet>
</template>

<script>
import {mapGetters} from 'vuex';
import {nl} from 'vuejs-datepicker/dist/locale';
import FileSelect from '../../components/fileSelect';
import firebase from 'firebase/app';
import 'firebase/storage';
import axios from 'axios';
import {bsnChangeMarker, maskBsn} from '@/utils/bsn';

export default {
  name: "AddContract",
  components: {FileSelect},
  data() {
    return {
      nl: nl,
      selectedCustomer: null,
      stamkaartBsn: '',
      bsnPrefilledFor: null,
      daysOfWeek: ['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag', 'zondag'],
      contract: {
        candidateId: null,
        firstName: '',
        lastName: '',
        bsn: '',
        isDutch: true,
        bsnDocument: null,
        customerId: null,
        shifts: '',
        shiftAddition: 0,
        function: '',
        startDate: '',
        scale: '',
        step: '',
        hourlyWage: 0,
        factor: null,
        workingHours: {
          maandag: { start: '06:00', end: '02:00', notWorking: false },
          dinsdag: { start: '06:00', end: '02:00', notWorking: false },
          woensdag: { start: '06:00', end: '02:00', notWorking: false },
          donderdag: { start: '06:00', end: '02:00', notWorking: false },
          vrijdag: { start: '06:00', end: '02:00', notWorking: false },
          zaterdag: { start: '06:00', end: '02:00', notWorking: false },
          zondag: { start: '06:00', end: '02:00', notWorking: false },
        },
        paymentFrequency: '',
        wkr: '',
        contractLanguage: null,
        idFront: null,
        idBack: null,
        bankCard: null,
        driversLicense: null,
        status: 'in behandeling',
        status_to_finished:null,
        remark: ''
      }
    };
  },
  computed: {
    ...mapGetters({
      customers: 'customers/getCustomers',
      getCustomerById: 'customers/getCustomer',
      candidates: 'candidates/getCandidates',
      getCandidateById: 'candidates/getCandidate',
      getCandidatePlacements: 'placements/getCandidatePlacements',
      shiftTypes: 'contracts/getShiftTypes',
      paymentFrequencies: 'contracts/getPaymentFrequencies',
      contractLanguages: 'contracts/getContractLanguages'
    }),
    activeCustomers() {
      return this.customers
          .filter(c => c.activeCustomer !== false)
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'nl', {sensitivity: 'base', numeric: true}));
    },
    bsnFromStamkaart() {
      return Boolean(this.stamkaartBsn) && this.contract.bsn === this.stamkaartBsn;
    },
    bsnDiffersFromStamkaart() {
      return Boolean(this.stamkaartBsn && this.contract.bsn) && this.contract.bsn !== this.stamkaartBsn;
    },
    maskedStamkaartBsn() {
      return maskBsn(this.stamkaartBsn);
    },
    hasRecentPlacement() {
      if (!this.contract.candidateId) return false;
      const placements = this.getCandidatePlacements(this.contract.candidateId);
      const fourWeeksAgo = window.moment().subtract(4, 'weeks');
      return placements.some(p => {
        const start = window.moment(p.startDate);
        const end = p.endDate ? window.moment(p.endDate) : null;
        return start.isSameOrBefore(window.moment()) && (!end || end.isAfter(fourWeeksAgo));
      });
    }
  },
  watch: {
    'contract.workingHours': {
      handler(newVal) {
        for (const day in newVal) {
          if (newVal[day].notWorking) {
            newVal[day].start = '';
            newVal[day].end = '';
          }
        }
      },
      deep: true
    },
    candidates(newVal, oldVal) {
      if (newVal.length > 0) {
        this.fetchCandidateFromUrl();
      }
    }
  },
  mounted() {
    this.fetchCandidateFromUrl();
  },
  methods: {
    fetchCandidateFromUrl() {
      const candidateId = this.$route.query.candidateId;
      if (candidateId && this.candidates.length > 0) {
        this.contract.candidateId = candidateId;
        this.onCandidateSelect(candidateId);
      }
    },
    incrementShiftAddition() {
      this.contract.shiftAddition = parseFloat((Number(this.contract.shiftAddition) + 0.01).toFixed(2));
    },
    decrementShiftAddition() {
      if (this.contract.shiftAddition > 0) {
        this.contract.shiftAddition = parseFloat((Number(this.contract.shiftAddition) - 0.01).toFixed(2));
      }
    },
    incrementHourlyWage() {
      this.contract.hourlyWage = parseFloat((Number(this.contract.hourlyWage) + 0.01).toFixed(2));
    },
    decrementHourlyWage() {
      if (this.contract.hourlyWage > 0) {
        this.contract.hourlyWage = parseFloat((Number(this.contract.hourlyWage) - 0.01).toFixed(2));
      }
    },
    incrementFactor() {
      this.contract.factor = parseFloat((Number(this.contract.factor) + 0.1).toFixed(2));
    },
    decrementFactor() {
      if (this.contract.factor > 0) {
        this.contract.factor = parseFloat((Number(this.contract.factor) - 0.1).toFixed(2));
      }
    },
    onCustomerSelect(customerId) {
      if (customerId) {
        const customer = this.getCustomerById(customerId);
        if (customer) {
          this.selectedCustomer = customer;
          this.contract.factor = customer.saleFactor;
        }
      }
    },
    onCandidateSelect(candidateId) {
      if (candidateId) {
        const candidate = this.getCandidateById(candidateId);
        if (candidate) {
          this.contract.firstName = candidate.firstName;
          this.contract.lastName = candidate.lastName;
          this.prefillBsn(candidateId, candidate.bsn || '');
        }
      }
    },
    prefillBsn(candidateId, stamkaartBsn) {
      const previousStamkaartBsn = this.stamkaartBsn;
      this.stamkaartBsn = stamkaartBsn;

      // Every realtime update of the candidates re-selects the same candidate; only a newly chosen one may replace the field.
      if (candidateId === this.bsnPrefilledFor) {
        return;
      }
      this.bsnPrefilledFor = candidateId;

      // A typed BSN survives the switch, but the previous candidate's stamkaart BSN never carries over to someone else.
      if (stamkaartBsn || this.contract.bsn === previousStamkaartBsn) {
        this.contract.bsn = stamkaartBsn;
      }
    },
    async copyBsnToStamkaart() {
      const candidate = this.getCandidateById(this.contract.candidateId);

      // Only an empty stamkaart is filled; a known BSN is changed by an admin on the stamkaart itself.
      if (!candidate || candidate.bsn) {
        return;
      }

      try {
        await this.$store.dispatch('candidates/patch', {
          id: this.contract.candidateId,
          bsn: this.contract.bsn,
          ...bsnChangeMarker(this.$store.getters.getUserData)
        });
      } catch (patchRefused) {
        this.$toasted.global.failedRequest({
          message: 'Contract aangemaakt, maar het BSN kon niet op de stamkaart worden gezet.'
        });
      }
    },
    async saveContract() {
      const result = await this.$validator.validate();
      if (!result) {
        this.$toasted.global.failedFormRequest();
        return;
      }


      try {

        const contractToSave = {
          ..._.omit(this.contract, ['idFront', 'idBack', 'bankCard', 'driversLicense', 'bsnDocument']),
          department: this.$store.getters.getUserData.selectedDepartment
        };

        const docId = await this.$store.dispatch('contracts/set', contractToSave);
        if (!docId) {
          return this.$toasted.global.failedRequest({message: 'Kon contract document niet aanmaken.'});
        }
        await this.copyBsnToStamkaart();
        const storage = firebase.storage();
        const uploadPromises = [];
        const filesToUpload = [
          {key: 'idFrontUrl', file: this.contract.idFront, name: 'idFront'},
          {key: 'idBackUrl', file: this.contract.idBack, name: 'idBack'},
          {key: 'bankCardUrl', file: this.contract.bankCard, name: 'bankCard'},
          {
            key: 'driversLicenseUrl',
            file: this.contract.driversLicense,
            name: 'driversLicense'
          },
          {key: 'bsnDocumentUrl', file: this.contract.bsnDocument, name: 'bsnDocument'}
        ];
     
        filesToUpload.forEach(item => {
          if (item.file) {
            const extension = item.file.name.split('.').pop();
            const path = `contracts/${docId}/${item.name}.${extension}`;
            const storageRef = storage.ref(path);
            const uploadTask = storageRef.put(item.file);
            uploadPromises.push(
                new Promise((resolve, reject) => {
                  uploadTask.on('state_changed',
                      () => {
                      },
                      error => reject(error),
                      () => {
                        uploadTask.snapshot.ref.getDownloadURL().then(downloadURL => {
                          resolve({[item.key]: downloadURL});
                        });
                      }
                  );
                })
            );
          }
        });
        this.$store.commit('setLoading',true)
        const uploadedFiles = await Promise.all(uploadPromises);
        const document = uploadedFiles.reduce((acc, url) => ({...acc, ...url}), {});

        if (Object.keys(document).length > 0) {
          await this.$store.dispatch('contracts/patch', {id: docId, ...document});
        }

        await axios.post(this.$CLOUD_API + '/contracts/created', {contractId: docId});
        this.$store.commit('setLoading',false)

        this.$router.push({name: 'contracts'});
        this.$toasted.global.successRequest({
          message: 'Contract aangemaakt'
        });
      } catch (error) {
        console.error(error);
        this.$toasted.global.failedRequest();
        this.$store.dispatch('setLoading',false)

      }
    }
  }
};
</script>
<style>
.custom-control-input:checked ~ .custom-control-label::before {
  color: #fff;
  border-color: #00A192;
  background-color: #00A192;
}
</style>